/**
 * Operations Tasks Types
 */

// =============================================================================
// Enums and Constants
// =============================================================================

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export const TASK_STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

// =============================================================================
// Core Types
// =============================================================================

export interface TaskComment {
  id: string;
  task_id: string;
  content: string;
  author_id: string;
  author_name: string;
  author_email?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  filename: string;
  original_filename: string;
  mime_type?: string;
  size_bytes?: number;
  storage_path: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  created_at: string;
  deleted_at?: string;
}

export interface TaskAuditLog {
  id: string;
  task_id: string;
  action: string;
  changes: Record<string, unknown>;
  actor_id?: string;
  actor_name?: string;
  actor_email?: string;
  created_at: string;
}

// =============================================================================
// Subtask Types
// =============================================================================

export interface SubtaskListItem {
  id: string;
  task_number: string;
  title: string;
  status: TaskStatus;
  due_date?: string;
  position: number;
  parent_task_id: string;
}

// =============================================================================
// Task Types
// =============================================================================

export interface Task {
  id: string;
  task_number: string;
  title: string;
  description?: string;

  // Project linking
  project_id?: string;
  quote_id?: string;
  project_number?: string;
  project_name?: string;
  milestone_key?: string;

  // Status and priority
  status: TaskStatus;
  priority: TaskPriority;

  // Assignment
  assigned_to_id?: string;
  assigned_to_name?: string;
  assigned_to_email?: string;
  created_by_id?: string;
  created_by_name?: string;

  // Scheduling
  due_date?: string;
  start_date?: string;
  completed_at?: string;
  completed_by_id?: string;
  completed_by_name?: string;

  // Time tracking
  estimated_hours?: number;
  actual_hours?: number;

  // Categorization
  tags: string[];

  // Kanban ordering
  position: number;

  // Comments (when fetching single task)
  comments?: TaskComment[];
  comments_count?: number;

  // Metadata
  created_at: string;
  updated_at: string;
  archived_at?: string;
}

export interface TaskListItem {
  id: string;
  task_number: string;
  title: string;
  description?: string;
  project_id?: string;
  quote_id?: string;
  project_number?: string;
  project_name?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigned_to_id?: string;
  assigned_to_name?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  tags: string[];
  position: number;
  comments_count: number;
  created_at: string;
  /** Subtasks (only populated when includeSubtasks=true) */
  subtasks?: SubtaskListItem[];
}

// =============================================================================
// Kanban Types
// =============================================================================

export interface KanbanColumn {
  status: TaskStatus;
  label: string;
  tasks: TaskListItem[];
}

export interface KanbanData {
  columns: KanbanColumn[];
  totalCount: number;
}

// =============================================================================
// API Request/Response Types
// =============================================================================

export type DueDatePreset = 'today' | 'this_week' | 'this_month';

export const DUE_DATE_PRESET_LABELS: Record<DueDatePreset, string> = {
  today: 'Due Today',
  this_week: 'Due This Week',
  this_month: 'Due This Month',
};

export interface TasksQueryParams {
  pageNumber?: number;
  pageLength?: number;
  sortKey?: string;
  sortDirection?: 1 | -1;
  status?: TaskStatus;
  statuses?: TaskStatus[];
  priority?: TaskPriority;
  projectId?: string;
  quoteId?: string;
  assignedToId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  dueDatePreset?: DueDatePreset;
  search?: string;
  includeArchived?: boolean;
  tags?: string[];
}

export interface KanbanQueryParams {
  projectId?: string;
  quoteId?: string;
  assignedToId?: string;
  priority?: TaskPriority;
  statuses?: TaskStatus[];
  search?: string;
  tags?: string[];
  dueDatePreset?: DueDatePreset;
}

export interface TasksResponse {
  tasks: TaskListItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
  pagination: {
    pageNumber: number;
    pageLength: number;
    totalRecords: number;
    totalPages: number;
  };
}

export interface TasksStats {
  total_count: number;
  by_status: Record<TaskStatus, number>;
  by_priority: Record<TaskPriority, number>;
  overdue_count: number;
  due_this_week: number;
  unassigned_count: number;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  project_id?: string;
  quote_id?: string;
  milestone_key?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigned_to_id?: string;
  assigned_to_name?: string;
  assigned_to_email?: string;
  due_date?: string;
  start_date?: string;
  estimated_hours?: number;
  tags?: string[];
  position?: number;
}

export interface UpdateTaskInput extends Partial<CreateTaskInput> {
  actual_hours?: number;
}

export interface UpdatePositionsInput {
  updates: Array<{
    id: string;
    status?: TaskStatus;
    position: number;
  }>;
}

// =============================================================================
// V4 API Response Types (camelCase from API)
// =============================================================================

export interface V4TasksResponse {
  tasks: V4TaskListItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export interface V4SubtaskListItem {
  id: string;
  taskNumber: string;
  title: string;
  status: string;
  dueDate?: string;
  position: number;
  parentTaskId: string;
}

export interface V4TaskListItem {
  id: string;
  taskNumber: string;
  title: string;
  description?: string;
  projectId?: string;
  quoteId?: string;
  projectNumber?: string;
  projectName?: string;
  status: string;
  priority: string;
  assignedToId?: string;
  assignedToName?: string;
  dueDate?: string;
  estimatedHours?: string;
  actualHours?: string;
  tags: string[];
  position: number;
  commentsCount: number;
  createdAt: string;
  /** Subtasks (only populated when includeSubtasks=true) */
  subtasks?: V4SubtaskListItem[];
}

export interface V4Task {
  id: string;
  taskNumber: string;
  title: string;
  description?: string;
  projectId?: string;
  quoteId?: string;
  projectNumber?: string;
  projectName?: string;
  milestoneKey?: string;
  status: string;
  priority: string;
  assignedToId?: string;
  assignedToName?: string;
  assignedToEmail?: string;
  createdById?: string;
  createdByName?: string;
  dueDate?: string;
  startDate?: string;
  completedAt?: string;
  completedById?: string;
  completedByName?: string;
  estimatedHours?: string;
  actualHours?: string;
  tags: string[];
  position: number;
  comments: V4TaskComment[];
  commentsCount: number;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface V4TaskComment {
  id: string;
  taskId: string;
  content: string;
  authorId: string;
  authorName: string;
  authorEmail?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface V4KanbanResponse {
  columns: Array<{
    status: string;
    label: string;
    tasks: V4TaskListItem[];
  }>;
  totalCount: number;
}

export interface V4TasksStatsResponse {
  totalCount: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  overdueCount: number;
  dueThisWeek: number;
  unassignedCount: number;
}

export interface V4AuditLogsResponse {
  logs: Array<{
    id: string;
    taskId: string;
    action: string;
    changes: Record<string, unknown>;
    actorId?: string;
    actorName?: string;
    actorEmail?: string;
    createdAt: string;
  }>;
}
